import {
  ATHLETE_CONTEXT_SCHEMA_VERSION,
  TIMELINE_FILTERS,
  buildAutomaticActivityStableSourceKey,
  createActivityCompletedTimelineEvent as createActivityCompletedContextEvent,
  createFtpChangeTimelineEvent as createFtpChangeContextEvent,
  createFtpSnapshotContextEvent as createFtpSnapshotContextEventBase,
  createRaceTimelineEvent as createRaceContextEvent,
  createRideAnalysisContextEvent as createRideAnalysisContextEventBase,
  createRideContextEvent as createRideContextEventBase,
  deleteAthleteContextEvent,
  displayTimelineProvenance,
  filterTimelineEvents,
  findEventsNearDate,
  getContextSummary,
  getRelatedContext,
  linkContextEvents,
  listAthleteContextEvents,
  reconcileAthleteContextDuplicateAutomaticEvents,
  timelineFilterLabel,
  toCalendarDateKey,
  upsertAthleteContextEvent,
  type ContextConfidence,
  type ContextEvent,
  type ContextEventSource,
  type ContextEventType,
  type ContextReconciliationResult,
  type ContextSummaryItem,
  type ContextUpsertResult,
  type TimelineFilter,
  type UpsertContextEventInput
} from "../context/athleteContext";
import type { AthleteProfile } from "../athlete-profile/types/athleteProfile";
import type { CompletedActivityRecord } from "./completedActivityHistory";

export const ATHLETE_TIMELINE_SCHEMA_VERSION = ATHLETE_CONTEXT_SCHEMA_VERSION;

export type AthleteTimelineEventType = ContextEventType;
export type TimelineEventSource = ContextEventSource;
export type TimelineConfidence = ContextConfidence;
export type AthleteTimelineEvent = ContextEvent & { eventType?: ContextEventType };
export type TimelineUpsertResult = Omit<ContextUpsertResult, "event"> & { event: AthleteTimelineEvent };
export type TimelineReconciliationResult = ContextReconciliationResult;
export type UpsertTimelineEventInput = Omit<UpsertContextEventInput, "type"> & { type?: ContextEventType; eventType?: ContextEventType };

function withLegacyEventType<T extends { type?: ContextEventType }>(event: T): T & { eventType?: ContextEventType } {
  return {
    ...event,
    eventType: event.type
  };
}

function normalizeInput(input: UpsertTimelineEventInput): UpsertContextEventInput {
  return {
    ...input,
    type: input.type ?? input.eventType ?? "manual_note"
  };
}

export async function listAthleteTimelineEvents(): Promise<AthleteTimelineEvent[]> {
  const events = await listAthleteContextEvents();
  return events.map(event => withLegacyEventType(event));
}

export async function upsertAthleteTimelineEvent(input: UpsertTimelineEventInput): Promise<TimelineUpsertResult> {
  const result = await upsertAthleteContextEvent(normalizeInput(input));
  return {
    ...result,
    event: withLegacyEventType(result.event)
  };
}

export async function reconcileAthleteTimelineDuplicateAutomaticEvents(): Promise<TimelineReconciliationResult> {
  return reconcileAthleteContextDuplicateAutomaticEvents();
}

export async function deleteAthleteTimelineEvent(id: string): Promise<void> {
  await deleteAthleteContextEvent(id);
}

export function createRideContextEvent(record: CompletedActivityRecord, options?: { timeZone?: string }): UpsertTimelineEventInput {
  return withLegacyEventType(createRideContextEventBase(record, options));
}

export function createActivityCompletedTimelineEvent(record: CompletedActivityRecord, options?: { timeZone?: string }): UpsertTimelineEventInput {
  return withLegacyEventType(createActivityCompletedContextEvent(record, options));
}

export function createRideAnalysisContextEvent(record: CompletedActivityRecord, options?: { timeZone?: string }): UpsertTimelineEventInput {
  return withLegacyEventType(createRideAnalysisContextEventBase(record, options));
}

export function createFtpSnapshotContextEvent(record: CompletedActivityRecord, options?: { timeZone?: string }): UpsertTimelineEventInput {
  return withLegacyEventType(createFtpSnapshotContextEventBase(record, options));
}

export function createRaceTimelineEvent(record: CompletedActivityRecord): UpsertTimelineEventInput {
  return withLegacyEventType(createRaceContextEvent(record));
}

export function createFtpChangeTimelineEvent(input: {
  profile: AthleteProfile;
  previousFtpWatts: number;
  nextFtpWatts: number;
}): UpsertTimelineEventInput {
  return withLegacyEventType(createFtpChangeContextEvent(input));
}

export {
  TIMELINE_FILTERS,
  buildAutomaticActivityStableSourceKey,
  displayTimelineProvenance,
  filterTimelineEvents,
  findEventsNearDate,
  getContextSummary,
  getRelatedContext,
  linkContextEvents,
  timelineFilterLabel,
  toCalendarDateKey,
  type ContextSummaryItem,
  type TimelineFilter
};
